import type {
  Activity,
  Asset,
  Chain as LiquidiumChain,
  LiquidiumState,
  Pool,
  UserPositionSummary,
  UserReserve,
} from '@liquidium/client'
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import LiquidiumClient, {
  LIQUIDIUM_ASSET_KEY,
  LIQUIDIUM_SIGNING_CHAIN,
} from '../clients/LiquidiumClient'
import useConfig from '../hooks/useConfig'
import useWallet from '../hooks/useWallet'
import { Chain, PlugToken } from '../interfaces/common'
import { logger } from '../logger'

/**
 * Liquidium reports every amount in an asset's own base units, alongside the
 * decimals to scale by, and prices its own USD values. Those are treated as the
 * source of truth here; Plug's token registry is consulted only to attach a logo
 * and a display symbol, and a miss is not an error.
 *
 * Tokens are matched by the registry's own `liquidium_asset` tag rather than by
 * symbol. Each pool is reachable from two directions — ckBTC or native BTC, USDC on
 * ICP or on Ethereum — and the registry holds more than one candidate per symbol
 * (both `USDC` and `ckusdc` exist as ICP token ids), so a symbol is not enough to
 * pick one. The tag is, and it keeps the pairing in the registry where a new pool
 * can be added without an app release.
 */

/** Scales a base-unit amount by its decimals into a display number. */
export const scaleAmount = (amount: bigint, decimals: bigint | number): number =>
  Number(amount) / 10 ** Number(decimals)

/** Basis points as a fraction, e.g. 6500bps -> 0.65. */
export const bpsToFraction = (bps: bigint): number => Number(bps) / 10_000

/**
 * Health factor as a plain number, or `undefined` when the profile has no debt.
 * Liquidium scales it by `healthFactorDecimals` (3, per HEALTH_FACTOR_SCALE) and
 * returns null rather than infinity when nothing is borrowed.
 */
export const toHealthFactor = (
  healthFactor: bigint | null,
  decimals: bigint
): number | undefined =>
  healthFactor === null ? undefined : Number(healthFactor) / 10 ** Number(decimals)

/**
 * One way of moving an asset in or out of a pool, paired with the Plug token that
 * represents it on that chain.
 *
 * A route is not a market. Each pool is reachable twice — natively and as its
 * chain-key equivalent — and both settle against the same pool at the same rates.
 * Routes exist so the user can pick which of their holdings to spend, or where funds
 * should land; the choice is about rails, not about which market they are entering.
 */
export interface LiquidiumRoute {
  pool: Pool
  token: PlugToken
  /** Chain the funds travel over. */
  route: LiquidiumChain
}

/**
 * A lending market: one per pool, and therefore one per asset.
 *
 * Deliberately carries no chain. `findPool` resolves every chain of an asset to the
 * same pool, so BTC and ckBTC are not two markets with coincidentally equal rates —
 * they are one market with two doors. Listing them separately showed every asset
 * twice with identical APYs.
 */
export interface LiquidiumMarket {
  pool: Pool
  /** Display token for the asset, for a logo and a symbol. Chain-agnostic. */
  token?: PlugToken
  /** Supply APR as a percentage. */
  supplyRate: number
  /** Borrow APR as a percentage. */
  borrowRate: number
}

/** A reserve row joined with its display token, when one could be resolved. */
export interface LiquidiumReserve {
  reserve: UserReserve
  token?: PlugToken
  /** Supplied amount in display units. */
  supplied: number
  /** Borrowed amount in display units. */
  borrowed: number
  suppliedUsd: number
  borrowedUsd: number
}

/**
 * States an activity can be in while still in flight. A supply lands in the
 * position a few minutes after its transfer — measured at about 2m54s for ckBTC —
 * so the UI has to represent that gap rather than assume the position is current.
 */
const PENDING_STATES: LiquidiumState[] = ['action_required', 'confirming', 'processing']

/**
 * How often to re-check while something is in flight.
 *
 * Split by chain because the waits are not comparable. An ICP deposit credits in a
 * few minutes, so a short interval keeps the position current. A native Bitcoin one
 * waits on block confirmations — tens of minutes — and polling that every fifteen
 * seconds would be hundreds of requests to watch something that changes roughly once
 * every ten.
 */
const PENDING_POLL_MS = 15_000
const SLOW_CHAIN_POLL_MS = 60_000

export interface LiquidiumContextType {
  /**
   * The wallet's Liquidium profile. Three states, and the distinction matters:
   * `undefined` while it is still being resolved, `null` once confirmed absent, and
   * the principal once it exists.
   *
   * Collapsing the first two would mean showing onboarding to someone who already has
   * a profile, for as long as the lookup takes. Liquidium requires a profile created
   * by signing with an ETH or BTC wallet before any position exists, so `null` is an
   * ordinary first-run state rather than a failure.
   */
  profileId: string | null | undefined
  /** Pools available to supply or borrow, independent of any profile. */
  pools: Pool[]
  /**
   * Every rail into and out of a pool, for the token pickers. Two entries per pool
   * where the asset exists both natively and as a chain-key token.
   */
  routes: LiquidiumRoute[]
  /**
   * One entry per pool, for the markets list. Independent of any profile, so it
   * renders before onboarding.
   */
  markets: LiquidiumMarket[]
  /** Per-asset supplied and borrowed rows. Empty without a profile. */
  reserves: LiquidiumReserve[]
  /** Aggregate collateral, debt, borrowing power. Undefined without a profile. */
  summary?: UserPositionSummary
  /** Undefined when there is no debt, or no profile. */
  healthFactor?: number
  /** Current USD prices keyed by Liquidium asset symbol. */
  prices: Record<string, number>
  /**
   * Deposits, repayments, borrows and withdrawals the protocol has not finished
   * yet, derived from its own activity feed rather than tracked locally. That means
   * they survive a reinstall and cannot drift from the protocol's view, at the cost
   * of a short gap right after a transfer before the protocol registers it.
   */
  pending: Activity[]
  loading: boolean
  error?: string
  refresh: () => void
  /**
   * Creates the profile for this wallet and refreshes. Requires an Ethereum
   * signature, which costs a chain-fusion canister call, and is effectively
   * one-way — only call it from a deliberate user action. Rejects when the wallet
   * has no Ethereum address.
   */
  createProfile: () => Promise<string>
}

export const LiquidiumContext = createContext<LiquidiumContextType>(null as any)

/**
 * Liquidium's chains overlap Plug's but are spelled differently, and only the ICP
 * and BTC ones correspond to a Plug `Chain` — Ethereum is modelled as an EVM chain
 * rather than a member of that enum.
 */
const plugChainFor = (chain: LiquidiumChain): Chain | undefined => {
  switch (chain) {
    case 'ICP':
      return Chain.ICP
    case 'BTC':
      return Chain.BTC
    default:
      return undefined
  }
}

/**
 * Ethereum mainnet. Liquidium's native ETH, USDC and USDT rails settle there and
 * nowhere else, so the same asset on another EVM chain is not a way into a pool.
 */
const ETHEREUM_CHAIN_ID = 1

/**
 * Whether a token is held on the rail it would travel over.
 *
 * Tagging is registry data, and this is what keeps a mistagged token from inventing a
 * rail the protocol cannot serve: USDC on Base would carry the same asset tag as the
 * Ethereum one, and supplying it would send funds nowhere.
 */
const isOnRail = (token: PlugToken, chain: LiquidiumChain): boolean => {
  const plugChain = plugChainFor(chain)
  return plugChain ? token.chain === plugChain : token.evmChain?.chainId === ETHEREUM_CHAIN_ID
}

/** Every token the registry tags as this asset, on any rail. */
const tokensForAsset = (tokens: PlugToken[], asset: Asset): PlugToken[] =>
  tokens.filter((token) => LiquidiumClient.assetOf(token) === asset)

/**
 * Chain-agnostic display token for a Liquidium asset — a logo and a symbol for a
 * pool, not a claim about where anything is held.
 *
 * Prefers the native token over the chain-key one. Both stand for the same asset,
 * but Plug's registry logos differ: the ck variants carry a chain-key badge, so
 * resolving the ledger first labelled a plain USDC position "ckUSDC" with the
 * chain-key mark on it — which reads as a debt that only ckUSDC can settle. A pool
 * belongs to no chain, so its display should not imply one.
 *
 * ICP lands on its ledger token either way: it is native to the chain the protocol
 * runs on, so an ICP-chain token is the only kind tagged for it.
 */
const findDisplayToken = (tokens: PlugToken[], asset: Asset): PlugToken | undefined => {
  const candidates = tokensForAsset(tokens, asset)
  return candidates.find((token) => token.chain !== Chain.ICP) ?? candidates[0]
}

/** Fixed-point pool rate as a percentage. */
const ratePercent = (raw: bigint, rateDecimals: bigint) =>
  (Number(raw) / 10 ** Number(rateDecimals)) * 100

const LiquidiumProvider = ({ children }: PropsWithChildren) => {
  const { wallet } = useWallet()
  const { tokens } = useConfig()

  const [profileId, setProfileId] = useState<string | null | undefined>(undefined)
  const [pools, setPools] = useState<Pool[]>([])
  const [reserves, setReserves] = useState<LiquidiumReserve[]>([])
  const [summary, setSummary] = useState<UserPositionSummary>()
  const [healthFactor, setHealthFactor] = useState<number>()
  const [prices, setPrices] = useState<Record<string, number>>({})
  const [pending, setPending] = useState<Activity[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  // The wallet's Ethereum address identifies its profile: ETH is the signing chain
  // Plug can produce, so the profile is keyed to that address.
  const signerAddress = LIQUIDIUM_SIGNING_CHAIN === 'ETH' ? wallet.ethAddress : undefined

  const load = useCallback(async () => {
    setLoading(true)
    setError(undefined)

    const client = new LiquidiumClient()

    try {
      // Market data does not depend on a profile, so it loads either way and gives
      // the borrow/supply screens something to show before onboarding.
      const [poolList, priceMap] = await Promise.all([
        client.market.listPools(),
        client.market.getAssetPrices(),
      ])

      setPools(poolList)
      setPrices(priceMap)

      if (!signerAddress) {
        // No Ethereum address means no profile can exist yet.
        setProfileId(null)
        setReserves([])
        setSummary(undefined)
        setHealthFactor(undefined)
        setPending([])
        return
      }

      const resolvedProfileId = await client.accounts.getProfileId(signerAddress)
      setProfileId(resolvedProfileId)

      if (!resolvedProfileId) {
        setReserves([])
        setSummary(undefined)
        setHealthFactor(undefined)
        setPending([])
        return
      }

      const [userReserves, positionSummary, activities] = await Promise.all([
        client.positions.getUserReserves(resolvedProfileId),
        client.positions.getUserPositionSummary(resolvedProfileId),
        // `active` is the in-flight filter. Asked for explicitly even though it is
        // the default, because the distinction matters: it excludes completed work,
        // which is exactly right here and exactly wrong for a history view.
        client.activities.list({ profileId: resolvedProfileId, filter: 'active' }).catch((e) => {
          // Activities come from the HTTP API rather than a canister, so they can
          // fail independently of the position reads. Losing the pending list is
          // not worth failing the whole load over.
          logger.debug(`Liquidium: could not load activities: ${String(e)}`)
          return [] as Activity[]
        }),
      ])

      setPending(activities.filter((a) => PENDING_STATES.includes(a.status.state)))

      setReserves(
        userReserves.map((reserve) => ({
          reserve,
          token: findDisplayToken(tokens, reserve.pool.asset),
          supplied: scaleAmount(reserve.position.deposited, reserve.position.depositedDecimals),
          borrowed: scaleAmount(reserve.position.borrowed, reserve.position.borrowedDecimals),
          suppliedUsd: scaleAmount(reserve.suppliedUsd, reserve.usdDecimals),
          borrowedUsd: scaleAmount(reserve.borrowedUsd, reserve.usdDecimals),
        }))
      )

      setSummary(positionSummary)
      // Taken from the summary rather than a separate getHealthFactor call: the
      // summary already derives it from the same single canister round-trip.
      setHealthFactor(
        toHealthFactor(positionSummary.healthFactor, positionSummary.healthFactorDecimals)
      )
    } catch (e) {
      logger.error('Liquidium: failed to load positions', { originalError: e })
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [signerAddress, tokens])

  useEffect(() => {
    load()
  }, [load])

  // Poll only while something is in flight. Crediting takes minutes, so there is no
  // point polling a settled position, and stopping when `pending` empties means the
  // steady state costs nothing.
  useEffect(() => {
    if (!pending.length) return

    // Anything waiting on Bitcoin sets the pace for the whole set; there is no point
    // polling quickly for one operation while another cannot change for ten minutes.
    const waitingOnBitcoin = pending.some((activity) => activity.chain === 'BTC')
    const interval = waitingOnBitcoin ? SLOW_CHAIN_POLL_MS : PENDING_POLL_MS

    const timer = setInterval(load, interval)
    return () => clearInterval(timer)
  }, [pending, load])

  /**
   * Expands pools into the rails that reach them. A route only appears when the
   * registry tags a token for it, so an asset Plug does not list is simply absent
   * rather than offering a selection that cannot be acted on.
   *
   * Native rails are listed first, which is what the token pickers default to and
   * what findDisplayToken shows for the same asset.
   */
  const routes = useMemo(
    () =>
      pools.flatMap((pool) => {
        const candidates = tokensForAsset(tokens, pool.asset)

        const poolRoutes: LiquidiumRoute[] = [
          ...candidates
            .filter((token) => token.chain !== Chain.ICP && isOnRail(token, pool.chain))
            .map((token) => ({ pool, token, route: pool.chain })),
          ...candidates
            .filter((token) => token.chain === Chain.ICP)
            .map((token) => ({ pool, token, route: 'ICP' as LiquidiumChain })),
        ]

        if (!poolRoutes.length) {
          // Only reachable through registry data, so no review catches it: an untagged
          // asset silently drops its market from the screen.
          logger.debug(
            `Liquidium: no token tagged ${LIQUIDIUM_ASSET_KEY}=${pool.asset}, so its pool has no rails`
          )
        }

        return poolRoutes
      }),
    [pools, tokens]
  )

  /** One market per pool, whether or not Plug lists a token for any of its rails. */
  const markets = useMemo(
    () =>
      pools.map((pool) => ({
        pool,
        token: findDisplayToken(tokens, pool.asset),
        supplyRate: ratePercent(pool.lendingRate, pool.rateDecimals),
        borrowRate: ratePercent(pool.borrowingRate, pool.rateDecimals),
      })),
    [pools, tokens]
  )

  const createProfile = useCallback(async () => {
    if (!signerAddress) {
      throw Error('This wallet has no Ethereum address, so it cannot sign for Liquidium')
    }

    const created = await new LiquidiumClient(wallet.principalId).createProfile(signerAddress)
    await load()

    return created
  }, [signerAddress, wallet.principalId, load])

  const value: LiquidiumContextType = {
    profileId,
    pools,
    routes,
    markets,
    reserves,
    summary,
    healthFactor,
    prices,
    loading,
    error,
    pending,
    refresh: load,
    createProfile,
  }

  return <LiquidiumContext.Provider value={value}>{children}</LiquidiumContext.Provider>
}

export default LiquidiumProvider
